import { useCallback, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import { differenceInCalendarDays, parseISO } from "date-fns";

import { formatDate } from "simplydo/core/dates";
import { initUseQuery } from "simplydo/core/query";
import {
  Button,
  Divider,
  Form,
  Icon,
  Loader,
  Message,
  Segment,
} from "simplydo/react/ui";

import api from "api";
import util from "utils/utils";

import { DateInput } from "components/lib/DateInputs";

type CalendarSettingsProps = {
  forType: "group" | "challenge";
  forId: string;
  calendarId: string;
};

type TargetUsers = "members" | "calendarAccess";

function getEarliestEventYmd(
  events: Array<{ from?: string; isGenerated?: boolean; eventType?: string }>,
): string | null {
  const dates = events
    .filter(
      (event) =>
        event.from && !event.isGenerated && event.eventType !== "background",
    )
    .map((event) => new Date(event.from!).toISOString().split("T")[0])
    .sort();
  return dates[0] ?? null;
}

type CalendarMoveWholeSectionProps = {
  calendarId: string;
  referenceStartYmd: string;
  invalidateCalendar: () => void;
};

const CalendarMoveWholeSection = ({
  calendarId,
  referenceStartYmd,
  invalidateCalendar,
}: CalendarMoveWholeSectionProps) => {
  const [proposedStartYmd, setProposedStartYmd] = useState(referenceStartYmd);

  const shiftDiffDays = useMemo(
    () =>
      differenceInCalendarDays(
        parseISO(proposedStartYmd),
        parseISO(referenceStartYmd),
      ),
    [referenceStartYmd, proposedStartYmd],
  );

  const shiftCalendar = useCallback(() => {
    if (shiftDiffDays === 0) {
      return;
    }
    api.calendars.shiftCalendarEvents(
      calendarId,
      { shiftDays: Math.abs(shiftDiffDays), shiftForward: shiftDiffDays > 0 },
      () => {
        toast.success("Calendar moved successfully");
        invalidateCalendar();
      },
      (error) => {
        toast.error(error.message);
      },
    );
  }, [shiftDiffDays, calendarId, invalidateCalendar]);

  return (
    <>
      <h5 style={{ marginBottom: 5 }}>Move whole calendar</h5>
      <p>
        Pick a new reference date. All events and milestones move by the same
        number of days so the schedule stays the same, just on different dates.
      </p>
      <Form>
        <Form.Field>
          <label htmlFor="calendarShiftStartDate">New reference date</label>
          <DateInput
            clearable
            id="calendarShiftStartDate"
            placeholder="Select a new reference date"
            value={proposedStartYmd}
            dateFormat="yyyy-MM-dd"
            onChange={(_e, { value }) =>
              setProposedStartYmd(String(value ?? ""))
            }
          />
        </Form.Field>
      </Form>
      {shiftDiffDays ? (
        <Message style={{ marginTop: 12 }}>
          <Message.Content>
            {shiftDiffDays > 0
              ? `Everything moves forward by ${util.pluralise(shiftDiffDays, "day", "days")} (reference becomes ${formatDate(parseISO(proposedStartYmd), "dd/MM/yyyy")}).`
              : `Everything moves back by ${util.pluralise(Math.abs(shiftDiffDays), "day", "days")} (reference becomes ${formatDate(parseISO(proposedStartYmd), "dd/MM/yyyy")}).`}
            <Divider />
            <Button onClick={shiftCalendar} content="Apply move" primary />
          </Message.Content>
        </Message>
      ) : null}
    </>
  );
};

const CalendarSettings = ({
  forType,
  forId,
  calendarId,
}: CalendarSettingsProps) => {
  const navigate = useNavigate();

  const calendarQuery = useQuery(
    initUseQuery(api.calendars.getCalendar, [calendarId], {
      isEnabled: !!calendarId,
    }),
  );

  const calendar = calendarQuery.data?.calendar;
  const targetUsers = (calendar?.targetUsers || "members") as TargetUsers;

  const referenceStartYmd = useMemo(() => {
    const fromEvents = getEarliestEventYmd(calendar?.events || []);
    if (fromEvents) {
      return fromEvents;
    }
    if (calendar?.start) {
      return new Date(calendar.start).toISOString().split("T")[0]!;
    }
    return new Date().toISOString().split("T")[0]!;
  }, [calendar]);

  const invalidateCalendar = useCallback(() => {
    api.invalidateQueries(api.calendars.getCalendar, [calendarId]);
  }, [calendarId]);

  const setTargetUsers = useCallback(
    (value: TargetUsers) => {
      api.calendars.update(
        calendarId,
        { targetUsers: value },
        () => {
          invalidateCalendar();
          api.invalidateQueries(api.calendars.get, [calendarId]);
          api.invalidateQueries(api.calendars.getAll, [forType, forId]);
        },
        (error) => {
          toast.error(error.message);
        },
      );
    },
    [calendarId, forId, forType, invalidateCalendar],
  );

  const getCalendarSyncUrl = useCallback(() => {
    api.calendars.getCalendarSyncUrl(
      calendarId,
      ({ syncUrl }) => {
        navigator.clipboard.writeText(syncUrl);
        toast.success("Calendar link copied to clipboard");
      },
      (error) => {
        toast.error(error.message);
      },
    );
  }, [calendarId]);

  const deleteCalendar = useCallback(() => {
    util
      .confirm(
        "Delete calendar?",
        "Deleting this calendar removes it for everyone in the group, including all scheduled events. This action cannot be undone. Are you sure?",
      )
      .then(() => {
        let navigateTo = "";
        if (forType === "group") {
          navigateTo = `/groups/${forId}/settings/calendars`;
        }
        api.calendars.delete(
          calendarId,
          () => {
            api.invalidateQueries(api.calendars.getAll, [forType, forId]);
            api.invalidateQueries(api.calendars.get, [calendarId]);
            api.invalidateQueries(api.calendars.getCalendar, [calendarId]);
            if (navigateTo) {
              navigate(navigateTo);
            }
          },
          (err) => toast.error(err.message),
        );
      })
      .catch(() => {});
  }, [calendarId, forType, forId, navigate]);

  if (calendarQuery.isLoading) {
    return (
      <Segment attached>
        <Loader active inline="centered" />
      </Segment>
    );
  }

  if (calendarQuery.isError) {
    return (
      <Segment attached>
        <Message negative>
          Could not load calendar settings. You may need calendar edit access.
        </Message>
        <Divider section />
        <Message warning>
          <Message.Header>Delete calendar</Message.Header>
          <Message.Content>
            Remove this calendar from the group. All scheduled events will be
            lost.
            <Divider />
            <Button
              basic
              onClick={() => deleteCalendar()}
              content="Delete"
              icon="trash"
            />
          </Message.Content>
        </Message>
      </Segment>
    );
  }

  return (
    <Segment attached style={{ marginTop: 0 }}>
      {forType === "challenge" ? (
        <>
          <CalendarMoveWholeSection
            key={`${calendarId}-${referenceStartYmd}`}
            calendarId={calendarId}
            referenceStartYmd={referenceStartYmd}
            invalidateCalendar={invalidateCalendar}
          />
        </>
      ) : null}
      <h5>Sync with your calendar</h5>
      <p>
        Subscribe in Google Calendar, Apple Calendar, Outlook, or other apps.
        Updates to this calendar will appear in your app.
      </p>
      <Button size="small" onClick={() => getCalendarSyncUrl()}>
        <Icon name="calendar" />
        Get calendar link
      </Button>

      {forType === "group" ? (
        <>
          <Divider section />
          <Message warning>
            <Message.Header>Delete calendar</Message.Header>
            <Message.Content>
              Deleting this calendar removes it for everyone, including all
              scheduled events. This action cannot be undone.
              <Divider />
              <Button
                basic
                onClick={() => deleteCalendar()}
                content="Delete"
                icon="trash"
              />
            </Message.Content>
          </Message>
        </>
      ) : null}
    </Segment>
  );
};

export default CalendarSettings;
